import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { motion } from 'framer-motion';
import LoadingSpinner from '../../../features/landing/shared/LoadingSpinner';
import { withAdminProtection } from '../../../utils/adminProtection';
import { useLocation, useNavigate } from 'react-router-dom';

const LeadTracker = ({ onSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const initialFormState = {
    leadName: '',
    phoneNumber: '',
    whatsappNumber: '',
    email: '',
    source: '',
    interestArea: '',
    followUpDate: new Date(),
    status: 'New',
    notes: '',
    lastContacted: new Date(),
    nextAction: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (location.state?.editLead) {
      const editLead = location.state.editLead;
      setFormData({
        ...editLead,
        // Check if the date is already a Date object
        followUpDate: editLead.followUpDate instanceof Date 
          ? editLead.followUpDate 
          : editLead.followUpDate?.toDate() || new Date(),
        lastContacted: editLead.lastContacted instanceof Date
          ? editLead.lastContacted
          : editLead.lastContacted?.toDate() || new Date()
      });
      setEditMode(true);
    }
  }, [location.state]);

  const sourceOptions = [
    'Pamphlet',
    'WhatsApp Ad',
    'Referral',
    'Facebook',
    'Website',
    'Other'
  ];

  const statusOptions = [
    'New',
    'Contacted',
    'Interested',
    'Not Interested',
    'Converted'
  ];

  const interestAreaOptions = [
    'Mutual Funds',
    'SIP',
    'Tax Saving',
    'Retirement Planning',
    'Child Education',
    'Portfolio Review',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const leadData = {
        ...formData,
        followUpDate: Timestamp.fromDate(formData.followUpDate),
        lastContacted: Timestamp.fromDate(formData.lastContacted),
        updatedAt: Timestamp.now()
      };

      if (editMode && location.state?.editLead?.id) {
        // Update existing lead
        const leadRef = doc(db, 'leads', location.state.editLead.id);
        await updateDoc(leadRef, leadData);
      } else {
        // Add new lead
        leadData.createdAt = Timestamp.now();
        await addDoc(collection(db, 'leads'), leadData);
      }

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        if (editMode) {
          navigate('/leads/manage');
        } else {
          setFormData(initialFormState);
          // Call onSuccess callback if provided (for modal use)
          onSuccess?.();
        }
      }, 2000);
    } catch (err) {
      setError('Failed to save lead: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/leads/manage');
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {error && (
        <div className="mb-4 p-4 text-red-600 bg-red-100 rounded-lg">
          {error}
        </div>
      )}

      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="mb-4 p-4 text-green-600 bg-green-100 rounded-lg"
        >
          Lead successfully {editMode ? 'updated' : 'added'}!
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg p-6"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">
            {editMode ? 'Edit Lead' : 'About you'}
          </h2>
          {editMode && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              name="leadName"
              value={formData.leadName}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">WhatsApp Number</label>
            <input
              type="tel"
              name="whatsappNumber"
              value={formData.whatsappNumber}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Source</label>
            <select
              name="source"
              value={formData.source}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              <option value="">Select Source</option>
              {sourceOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Interest Area</label>
            <select
              name="interestArea"
              value={formData.interestArea}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              <option value="">Select Interest Area</option>
              {interestAreaOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Follow Up Date</label>
            <DatePicker
              selected={formData.followUpDate}
              onChange={(date) => handleDateChange(date, 'followUpDate')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              dateFormat="MMMM d, yyyy"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              {statusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Next Action</label>
            <input
              type="text"
              name="nextAction"
              value={formData.nextAction}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div className="mt-6 flex justify-end space-x-4">
            <button
              type="submit"
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <LoadingSpinner size="small" />
                  <span className="ml-2">Saving...</span>
                </span>
              ) : (
                editMode ? 'Update Lead' : 'Submit Details'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default withAdminProtection(LeadTracker);
